import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, Space, Tag } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { IconEdit, IconPlus } from '@arco-design/web-react/icon';
import { get } from 'lodash';

import { getActiveModelSourceType, getModelSourceSummary } from '@/delivery-type-plugin/capabilities/maas-init/utils/display';
import { MODEL_CATEGORY_FORM_FIELD, normalizeModelCategory } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelCategory';
import type { MaasModel } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { parseStorageQuantityToGi } from '@/delivery-type-plugin/capabilities/maas-model-storage/quantity';
import { MaasDataDiskMode } from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { useResourceSectionReadOnly } from '@/delivery-type-plugin/components';
import type { ModelCapabilityModalityKey } from '@/types/model-capabilities';

import styles from './model-source-setting.module.less';
import { renderModelCategoryTags } from './render-model-category';

type LegacyModelDraft = MaasModel & {
  name?: string;
  size?: string;
  source?: Record<string, unknown>;
};

/** 合并表单值与初始快照，保证未注册字段也能展示（旧版单源弹窗草稿） */
export function resolveModelSnapshot(form: FormInstance, initModel?: MaasModel, watchedModel?: MaasModel): LegacyModelDraft {
  const nameFromField = form.getFieldValue('model.name') as string | undefined;
  const sizeFromField = form.getFieldValue('model.size') as string | undefined;
  const sourceFromField = form.getFieldValue('model.source') as Record<string, unknown> | undefined;
  const legacyInit = initModel as LegacyModelDraft | undefined;
  const legacyWatched = watchedModel as LegacyModelDraft | undefined;

  return {
    ...legacyInit,
    ...legacyWatched,
    mountPath: legacyWatched?.mountPath ?? legacyInit?.mountPath ?? '/models',
    sources: legacyWatched?.sources ?? legacyInit?.sources ?? [],
    name: legacyWatched?.name ?? nameFromField ?? legacyInit?.name,
    size: legacyWatched?.size ?? sizeFromField ?? legacyInit?.size,
    source: legacyWatched?.source ?? sourceFromField ?? legacyInit?.source,
  };
}

interface ModelAddCardProps {
  onClick: () => void;
}

/** 未配置模型时的添加卡片 */
export function ModelAddCard({ onClick }: ModelAddCardProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  if (sectionReadOnly) return null;
  return (
    <div
      className={styles.addCard}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <IconPlus className={styles.addIcon} />
      <span className={styles.addTitle}>{t('data-center:maas.add.model')}</span>
      <span className={styles.addDesc}>{t('data-center:maas.model.card.placeholder')}</span>
    </div>
  );
}

interface ModelInfoCardProps {
  modelName?: string;
  modelCategory?: ModelCapabilityModalityKey[];
  sourceSummary: string;
  sourceDetail?: string;
  modelSize?: string;
  onEdit: () => void;
  /** 来源选择弹窗内展示必填标记 */
  showRequiredTag?: boolean;
  /** 空态标题（存储区块未配置时使用） */
  emptyTitle?: string;
  /** 空态说明 */
  emptyDesc?: string;
  /** 已有 PVC 模式：仅展示名称与大小 */
  isExistingMode?: boolean;
}

/** 模型信息展示卡片（来源选择与存储摘要共用） */
export function ModelInfoCard({
  modelName,
  modelCategory,
  sourceSummary,
  sourceDetail,
  modelSize,
  onEdit,
  showRequiredTag,
  emptyTitle,
  emptyDesc,
  isExistingMode,
}: ModelInfoCardProps) {
  const { t } = useTranslation();

  if (emptyTitle) {
    return (
      <div className={styles.displayCard}>
        <div className={styles.displayCardMain}>
          <div className={styles.displayCardMeta}>
            <div className={styles.displayCardName}>{emptyTitle}</div>
            {emptyDesc ? <div className={styles.displayCardSource}>{emptyDesc}</div> : null}
          </div>
          {/* <Button type="text" size="small" icon={<IconEdit />} onClick={onEdit}>
            {t('common:edit')}
          </Button> */}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.displayCard}>
      <div className={styles.displayCardMain}>
        <div className={styles.displayCardMeta}>
          <div className={styles.displayCardTitleRow}>
            <span className={styles.displayCardName}>{modelName || sourceSummary || t('data-center:maas.section.model')}</span>
            {showRequiredTag ? (
              <Tag size="small" color="arcoblue">
                {t('common:required')}
              </Tag>
            ) : null}
          </div>
          {Array.isArray(modelCategory) && modelCategory.length ? (
            <div className={styles.displayCardSource}>
              {t('data-center:model.type')}: {renderModelCategoryTags(modelCategory)}
            </div>
          ) : null}
          {isExistingMode ? (
            modelSize ? (
              <div className={styles.displayCardSource}>
                {t('data-center:maas.model.size')}: {modelSize}
              </div>
            ) : null
          ) : (
            <>
              <div className={styles.displayCardSource}>{sourceSummary}</div>
              {sourceDetail && sourceDetail !== sourceSummary ? (
                <div className={styles.displayCardDetail}>
                  {sourceDetail}
                  {modelSize ? ` · ${modelSize}` : ''}
                </div>
              ) : modelSize ? (
                <div className={styles.displayCardDetail}>{modelSize}</div>
              ) : null}
            </>
          )}
        </div>
        {/* <Button type="text" size="small" icon={<IconEdit />} onClick={onEdit}>
          {t('common:edit')}
        </Button> */}
      </div>
    </div>
  );
}

interface MaasModelSummaryCardProps {
  form: FormInstance;
  mode: MaasDataDiskMode;
  onEdit: () => void;
  /** 编辑/URL 回填的模型快照（model.* 未挂载 Form.Item 时作为展示兜底） */
  initModel?: MaasModel;
  /** 编辑回填的模型类型（annotations） */
  initModelCategory?: string[];
}

/** 存储卡片下方的模型信息摘要 */
export function MaasModelSummaryCard({ form, mode, onEdit, initModel, initModelCategory }: MaasModelSummaryCardProps) {
  const { t } = useTranslation();
  const watchedModel = Form.useWatch('model', form) as MaasModel | undefined;
  const watchedModelCategory = Form.useWatch(MODEL_CATEGORY_FORM_FIELD, form) as string[] | undefined;

  const model = useMemo(() => resolveModelSnapshot(form, initModel, watchedModel), [form, initModel, watchedModel]);

  const activeSourceType = useMemo(() => getActiveModelSourceType(model.source), [model.source]);
  const sourceSummary = useMemo(() => getModelSourceSummary(model.source, t, model.name), [model.name, model.source, t]);

  const sourceDetail = useMemo(() => {
    if (!activeSourceType || !model.source) return '';
    const block = model.source[activeSourceType];
    if (!block || typeof block !== 'object') return '';
    return String(get(block, 'url') || get(block, 'model') || '').trim();
  }, [activeSourceType, model.source]);

  const modelSizeLabel = useMemo(() => {
    if (!model.size) return undefined;
    const gi = parseStorageQuantityToGi(model.size);
    if (gi !== undefined && gi > 0) return `${gi}Gi`;
    return model.size;
  }, [model.size]);

  const modelCategory = useMemo(() => {
    const current = normalizeModelCategory(watchedModelCategory);
    if (current.length) return current;
    return normalizeModelCategory(initModelCategory);
  }, [initModelCategory, watchedModelCategory]);

  const isExistingMode = mode === MaasDataDiskMode.Existing;
  const hasModelInfo = isExistingMode
    ? !!String(model.name || '').trim() || !!modelSizeLabel
    : !!String(model.name || '').trim() || !!activeSourceType || !!modelSizeLabel;

  if (!hasModelInfo) {
    return (
      <ModelInfoCard
        sourceSummary=""
        onEdit={onEdit}
        emptyTitle={t('data-center:maas.section.model')}
        emptyDesc={t('data-center:maas.storage.configure.first')}
      />
    );
  }

  return (
    <ModelInfoCard
      modelName={model.name}
      modelCategory={modelCategory}
      sourceSummary={sourceSummary}
      sourceDetail={sourceDetail}
      modelSize={modelSizeLabel}
      onEdit={onEdit}
      isExistingMode={isExistingMode}
    />
  );
}
